/**
 * PostHog Analytics Gateway Implementation
 *
 * Real implementation of IAnalyticsGateway using PostHog SDK.
 *
 * IMPORTANT: Requires posthog-js package to be installed.
 * Install with: pnpm add posthog-js
 *
 * GDPR Compliance:
 * - Disables automatic capture (autocapture: false)
 * - Disables automatic pageview/pageleave capture
 * - Opts out by default (opt_out_capturing_by_default: true)
 * - Uses localStorage for persistence
 *
 * Story 4.1: PostHog Frontend Analytics
 */
import posthog from "posthog-js";
import type {
  IAnalyticsGateway,
  AnalyticsInitOptions,
} from "@domain/ports/IAnalyticsGateway";
export class PostHogAnalyticsGateway implements IAnalyticsGateway {
  private initialized = false;

  /**
   * Initialize PostHog SDK
   *
   * Configures PostHog with GDPR-compliant settings:
   * - No automatic capture (custom events only)
   * - Opt-out by default (user must consent)
   * - Debug mode in development
   */
  init(apiKey: string, options?: AnalyticsInitOptions): void {
    if (this.initialized) return;

    posthog.init(apiKey, {
      api_host: options?.host || "https://app.posthog.com",
      // CRITICAL: Disable automatic capture - custom events only
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      // GDPR: Opt-out by default, user must consent
      opt_out_capturing_by_default: true,
      persistence: "localStorage",
      debug: options?.debug ?? import.meta.env.DEV,
    });

    this.initialized = true;
  }

  /**
   * Identify user for event association
   */
  identify(userId: string, properties?: Record<string, unknown>): void {
    posthog.identify(userId, properties);
  }

  /**
   * Capture custom event
   *
   * Events are only captured if user has opted in.
   */
  capture(event: string, properties?: Record<string, unknown>): void {
    posthog.capture(event, properties);
  }

  /**
   * Reset user identification
   *
   * Clears user association for subsequent events.
   */
  reset(): void {
    posthog.reset();
  }

  /**
   * Opt-in to analytics tracking
   */
  optIn(): void {
    posthog.opt_in_capturing();
  }

  /**
   * Opt-out of analytics tracking
   */
  optOut(): void {
    posthog.opt_out_capturing();
  }

  /**
   * Check if user has opted out
   */
  isOptedOut(): boolean {
    return posthog.has_opted_out_capturing();
  }
}
